import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Tabs,
  Tab,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import { useAuth } from '../../hooks/useAuth';
import api from '../../services/api';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`account-tabpanel-${index}`}
      aria-labelledby={`account-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const AccountSettings: React.FC = () => {
  const { user } = useAuth();
  const [tabValue, setTabValue] = useState(0);

  // Password change state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');

  // Email change state
  const [emailCurrentPassword, setEmailCurrentPassword] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [emailLoading, setEmailLoading] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [emailSuccess, setEmailSuccess] = useState('');

  // Account deletion state
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    // Clear errors when switching tabs
    setPasswordError('');
    setPasswordSuccess('');
    setEmailError('');
    setEmailSuccess('');
    setDeleteError('');
  };

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordError('');
    setPasswordSuccess('');

    if (newPassword !== confirmPassword) {
      setPasswordError('New passwords do not match');
      return;
    }

    setPasswordLoading(true);

    try {
      await api.patch('/users/password', {
        current_password: currentPassword,
        new_password: newPassword,
      });

      setPasswordSuccess('Password updated successfully');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error: any) {
      setPasswordError(
        error.response?.data?.message || 'Failed to update password'
      );
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleEmailChange = async (e: React.FormEvent) => {
    e.preventDefault();
    setEmailError('');
    setEmailSuccess('');

    setEmailLoading(true);

    try {
      await api.post('/users/email/request-change', {
        new_email: newEmail,
        current_password: emailCurrentPassword,
      });

      setEmailSuccess(
        'Verification email sent! Please check your new email to confirm the change.'
      );
      setEmailCurrentPassword('');
      setNewEmail('');
    } catch (error: any) {
      setEmailError(
        error.response?.data?.message || 'Failed to request email change'
      );
    } finally {
      setEmailLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    setDeleteError('');
    setDeleteLoading(true);

    try {
      // SSO users (Google/Apple) have no password to verify — backend skips
      // the password check when user.password is NULL, so we send an empty value.
      await api.post('/users/account/request-deletion', {
        current_password: user?.hasPassword ? deletePassword : '',
      });

      setDeleteDialogOpen(false);
      alert(
        'Verification email sent! Please check your email to confirm account deletion.'
      );
      setDeletePassword('');
    } catch (error: any) {
      setDeleteError(
        error.response?.data?.message || 'Failed to request account deletion'
      );
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" gutterBottom>
        Account Settings
      </Typography>

      <Paper sx={{ mt: 3 }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          aria-label="account settings tabs"
        >
          <Tab label="Change Password" />
          <Tab label="Change Email" />
          <Tab label="Delete Account" />
        </Tabs>

        {/* Change Password Tab */}
        <TabPanel value={tabValue} index={0}>
          <Typography variant="h6" gutterBottom>
            Change Password
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Password must be at least 8 characters with 1 capital letter, 1
            number, and 1 special character.
          </Typography>

          {passwordError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {passwordError}
            </Alert>
          )}
          {passwordSuccess && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {passwordSuccess}
            </Alert>
          )}

          <Box component="form" onSubmit={handlePasswordChange}>
            <TextField
              fullWidth
              type="password"
              label="Current Password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              required
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              type="password"
              label="New Password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              type="password"
              label="Confirm New Password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              sx={{ mb: 2 }}
            />
            <Button
              type="submit"
              variant="contained"
              disabled={passwordLoading}
              startIcon={passwordLoading && <CircularProgress size={20} />}
            >
              Update Password
            </Button>
          </Box>
        </TabPanel>

        {/* Change Email Tab */}
        <TabPanel value={tabValue} index={1}>
          <Typography variant="h6" gutterBottom>
            Change Email
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Current email: <strong>{user?.email}</strong>
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            You will receive a verification email to confirm the change.
          </Typography>

          {emailError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {emailError}
            </Alert>
          )}
          {emailSuccess && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {emailSuccess}
            </Alert>
          )}

          <Box component="form" onSubmit={handleEmailChange}>
            <TextField
              fullWidth
              type="email"
              label="New Email"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              required
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              type="password"
              label="Current Password"
              value={emailCurrentPassword}
              onChange={(e) => setEmailCurrentPassword(e.target.value)}
              required
              sx={{ mb: 2 }}
            />
            <Button
              type="submit"
              variant="contained"
              disabled={emailLoading}
              startIcon={emailLoading && <CircularProgress size={20} />}
            >
              Request Email Change
            </Button>
          </Box>
        </TabPanel>

        {/* Delete Account Tab */}
        <TabPanel value={tabValue} index={2}>
          <Typography variant="h6" gutterBottom color="error">
            Delete Account
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            This action will deactivate your account. Your data will be
            preserved in case you want to reactivate later.
          </Typography>
          <Alert severity="error" sx={{ mb: 2 }}>
            <strong>Important:</strong> Deleting your account does NOT cancel your subscription.
            If you have an active subscription, please cancel it first in the{' '}
            <a href="/membership" style={{ color: 'inherit' }}>Membership</a> page
            by clicking "Manage Billing".
          </Alert>
          <Alert severity="warning" sx={{ mb: 2 }}>
            You will receive a confirmation email before your account is deleted.
          </Alert>

          {deleteError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {deleteError}
            </Alert>
          )}

          <Button
            variant="outlined"
            color="error"
            onClick={() => setDeleteDialogOpen(true)}
          >
            Delete My Account
          </Button>
        </TabPanel>
      </Paper>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => !deleteLoading && setDeleteDialogOpen(false)}
      >
        <DialogTitle>Confirm Account Deletion</DialogTitle>
        <DialogContent>
          <Alert severity="error" sx={{ mb: 2 }}>
            This will NOT cancel your subscription. Cancel it first via "Manage Billing" on the Membership page.
          </Alert>
          <DialogContentText sx={{ mb: 2 }}>
            Are you sure you want to delete your account? This action will
            deactivate your account and you will need to contact support to
            reactivate it.
          </DialogContentText>
          {user?.hasPassword ? (
            <TextField
              fullWidth
              type="password"
              label="Enter Your Password"
              value={deletePassword}
              onChange={(e) => setDeletePassword(e.target.value)}
              required
            />
          ) : (
            <DialogContentText sx={{ fontStyle: 'italic' }}>
              You signed in with {user?.authProvider === 'apple' ? 'Apple' : user?.authProvider === 'google' ? 'Google' : 'a social provider'}, so no password is needed. We'll send a confirmation email — click the link there to permanently delete your account.
            </DialogContentText>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            disabled={deleteLoading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteAccount}
            color="error"
            variant="contained"
            disabled={deleteLoading || (user?.hasPassword && !deletePassword)}
            startIcon={deleteLoading && <CircularProgress size={20} />}
          >
            Delete Account
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AccountSettings;
